import { ReactNode, startTransition, useState } from "react";

type Direction = "fwd" | "bak";

/**
 * The screen-change animation from the You canvas: the incoming screen slides in and
 * fades — from the right going deeper into a section, from the left coming back out —
 * and only the incoming screen moves. Duration and easing live in the theme's motion
 * tokens; reduced motion turns it off.
 *
 * The direction is announced by whoever navigates, right before the route change, and
 * consumed once by the destination as it mounts. A navigation nobody announced (a deep
 * link, the browser's own back) animates nothing, which is what an unannounced arrival
 * should do.
 */
let pending: { dir: Direction; at: number } | null = null;

export function setScreenDirection(dir: Direction) {
  pending = { dir, at: Date.now() };
}

function consumeDirection(): Direction | null {
  // The announcement survives only a moment — a direction set for a navigation that
  // never landed must not replay on some later, unrelated mount.
  const dir = pending && Date.now() - pending.at < 1000 ? pending.dir : null;
  pending = null;
  return dir;
}

/**
 * Navigate between account screens. The route change runs in a transition, so when
 * the destination's chunk is still loading React keeps the current screen up instead
 * of flashing the Suspense loader — with the idle preload in the Router this is a
 * belt over braces, but the first tap can beat the idle callback.
 */
export function navigateSection(
  navigate: (to: any, opts?: any) => void,
  to: any,
  dir: Direction | null,
  opts?: any,
) {
  if (dir) setScreenDirection(dir);
  startTransition(() => navigate(to, opts));
}

function ScreenTransition({ children }: { children: ReactNode }) {
  const [dir] = useState(consumeDirection);
  if (!dir) return <>{children}</>;
  return (
    <div
      className={
        "w-full " +
        (dir === "fwd" ? "animate-screen-fwd" : "animate-screen-bak") +
        " motion-reduce:animate-none"
      }
    >
      {children}
    </div>
  );
}

export default ScreenTransition;
